import { Maybe } from '@crocodille/common';
import { RefObject, useState } from 'react';
import { useCallback } from 'react';
import { useRef } from 'react';

import { useScroll } from './use-scroll';

/**
 * Provides a flag indicating when container is being scrolled.
 *
 * @param targetRef Target element
 * @param debounceTime Time to keep the scrolling flag enabled after scrolling stops
 *
 * @returns Whether the container is scrolling or not
 */
export function useScrollState(
  targetRef:
    | RefObject<HTMLElement | null>
    | Maybe<Window | Document> = typeof window !== 'undefined'
    ? document
    : null,
  debounceTime = 100,
) {
  const [isScrolling, setScrolling] = useState(false);
  const debounceTimeoutRef = useRef<Maybe<NodeJS.Timeout>>(null);

  const clearScrolling = useCallback(() => {
    const timeout = debounceTimeoutRef.current;

    if (timeout) {
      clearTimeout(timeout);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setScrolling(false);

      debounceTimeoutRef.current = null;
    }, debounceTime);
  }, [debounceTime]);

  useScroll(() => {
    setScrolling(true);
    clearScrolling();
  }, targetRef);

  return isScrolling;
}
